import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import {
    UsersThree, Stethoscope, User, Warning, ArrowRight, Phone, Envelope,
} from "@phosphor-icons/react";

/* ============================================================================
   CoeReactivationTab — single-screen triage for everyone currently
   deactivated across COE staff, patients, and providers. Shows WHO
   deactivated them, WHEN, WHY (from the audit log), and the in-flight
   work they were holding — so admin can decide who comes back online.
   ========================================================================== */

const KIND_META = {
    coe: { label: "COE", Icon: UsersThree, cls: "bg-primary/10 text-primary border-primary/20" },
    patient: { label: "Patient", Icon: User, cls: "bg-sky-100 text-sky-700 border-sky-200" },
    doctor: { label: "Provider", Icon: Stethoscope, cls: "bg-violet-100 text-violet-700 border-violet-200" },
};

const fmtDT = (s) => s
    ? new Date(s).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
    : "—";

const daysSince = (iso) => {
    if (!iso) return null;
    const d = (Date.now() - new Date(iso).getTime()) / 86400000;
    return Math.floor(d);
};

const initials = (n) => (n || "?").split(/\s+/).map((s) => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();

export function CoeReactivationTab() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [kindFilter, setKindFilter] = useState("all");

    useEffect(() => {
        api.get("/admin/coe/reactivation-queue")
            .then((r) => setData(r.data))
            .finally(() => setLoading(false));
    }, []);

    const rows = useMemo(() => {
        if (!data) return [];
        return data.rows.filter((r) => kindFilter === "all" || r.kind === kindFilter);
    }, [data, kindFilter]);

    if (loading) return <div className="text-muted-foreground text-sm">Loading reactivation queue…</div>;
    if (!data || data.count === 0) {
        return (
            <div className="rounded-2xl border border-border bg-card p-10 text-center" data-testid="coe-reactivation-empty">
                <UsersThree size={28} className="mx-auto text-emerald-500 mb-2" weight="fill" />
                <div className="font-display text-lg font-bold">Nobody is deactivated right now.</div>
                <p className="text-xs text-muted-foreground mt-1">When admins deactivate a user, patient, or provider, they'll appear here for review.</p>
            </div>
        );
    }

    return (
        <div className="space-y-5" data-testid="coe-reactivation-tab">
            {/* Summary tiles */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <SummaryTile label="Total deactivated" value={data.count} active={kindFilter === "all"} onClick={() => setKindFilter("all")} />
                <SummaryTile label="COE users"   value={data.by_kind.coe}     active={kindFilter === "coe"}     onClick={() => setKindFilter("coe")} />
                <SummaryTile label="Patients"    value={data.by_kind.patient} active={kindFilter === "patient"} onClick={() => setKindFilter("patient")} />
                <SummaryTile label="Providers"   value={data.by_kind.doctor}  active={kindFilter === "doctor"}  onClick={() => setKindFilter("doctor")} />
            </div>

            <div className="rounded-2xl border border-border bg-card overflow-hidden">
                <div className="hidden md:grid grid-cols-12 gap-3 px-5 py-3 text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground border-b border-border bg-secondary/30">
                    <div className="col-span-3">Person</div>
                    <div className="col-span-3">Reason for deactivation</div>
                    <div className="col-span-3">Deactivated</div>
                    <div className="col-span-2">Work held</div>
                    <div className="col-span-1 text-right">Action</div>
                </div>
                {rows.map((r) => {
                    const km = KIND_META[r.kind] || KIND_META.coe;
                    const since = daysSince(r.deactivated_at);
                    return (
                        <Link
                            key={`${r.kind}-${r.id}`}
                            to={r.profile_url}
                            className="grid grid-cols-1 md:grid-cols-12 gap-3 px-5 py-3 border-b border-border last:border-0 items-center hover:bg-secondary/30 transition"
                            data-testid={`coe-reactivation-row-${r.kind}-${r.id}`}
                        >
                            <div className="md:col-span-3 flex items-center gap-3 min-w-0">
                                <div className="w-9 h-9 rounded-full bg-secondary text-foreground/70 font-semibold grid place-items-center shrink-0">
                                    {initials(r.name)}
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                        <div className="font-semibold truncate">{r.name}</div>
                                        <span className={`text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded border font-bold inline-flex items-center gap-1 ${km.cls}`}>
                                            <km.Icon size={9} weight="fill" /> {km.label}
                                        </span>
                                    </div>
                                    <div className="text-[10px] text-muted-foreground truncate">{r.subtitle}</div>
                                </div>
                            </div>
                            <div className="md:col-span-3 text-xs min-w-0">
                                {r.reason ? (
                                    <span className="italic text-foreground/80 line-clamp-2" title={r.reason}>"{r.reason}"</span>
                                ) : (
                                    <span className="inline-flex items-center gap-1 text-amber-700 font-medium">
                                        <Warning size={11} weight="fill" /> Reason not captured (pre-audit)
                                    </span>
                                )}
                            </div>
                            <div className="md:col-span-3 text-xs min-w-0">
                                <div className="text-foreground">{fmtDT(r.deactivated_at)}</div>
                                <div className="text-[10px] text-muted-foreground mt-0.5">
                                    {since != null && (
                                        <span className={since >= 30 ? "text-rose-700 font-semibold" : since >= 7 ? "text-amber-700 font-semibold" : ""}>
                                            {since === 0 ? "today" : `${since} day${since === 1 ? "" : "s"} ago`}
                                        </span>
                                    )}
                                    {r.deactivated_by && <span className="ml-1">· by {r.deactivated_by}</span>}
                                </div>
                            </div>
                            <div className="md:col-span-2 text-xs">
                                <div className="font-semibold text-foreground tabular-nums">{r.open_work_count}</div>
                                <div className="text-[10px] text-muted-foreground">{r.open_work_label}</div>
                            </div>
                            <div className="md:col-span-1 flex md:justify-end">
                                <ArrowRight size={13} className="text-muted-foreground" />
                            </div>
                        </Link>
                    );
                })}
            </div>
        </div>
    );
}

function SummaryTile({ label, value, active, onClick }) {
    return (
        <button type="button" onClick={onClick}
            className={`text-left rounded-xl border bg-card p-3 transition hover:shadow-sm ${active ? "border-foreground ring-2 ring-foreground/20" : "border-border"}`}>
            <div className="text-2xl font-display font-bold tabular-nums leading-none">{value}</div>
            <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-muted-foreground mt-1">{label}</div>
        </button>
    );
}
